import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ChevronRight, Search, SlidersHorizontal, X } from "lucide-react";

import { ModeToggle } from "@/components/ModeToggle";
import { PartnerBannerCarousel } from "@/components/business/PartnerBannerCarousel";
import {
  BusinessListingCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  EventSkeleton,
  ServiceCard,
  ServiceSkeleton,
} from "@/components/business/BusinessDirectory";


import {
  CategoryTileGrid,
  CategoryTileGridSkeleton,
  EventsPanel,
} from "@/components/business/BusinessMobile";
import { PlaceCard } from "@/components/PlaceCard";

import { AutoT } from "@/i18n/AutoT";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCity } from "@/context/CityContext";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { useResolvedCity } from "@/hooks/use-public-cities";
import { businessGetPublicHome } from "@/lib/business-public.functions";
import { businessTrackEvent } from "@/lib/business-track.functions";
import { analyticsSessionId } from "@/lib/analytics-session";
import {
  AFTER_WORK_LANES,
  PRIMARY_LANES,
  findLane,
  laneItems,
  type BusinessLaneKey,
} from "@/lib/business-lanes";

type BusinessSearch = { lane?: string };

/**
 * Business admin taxonomy keys mapped onto the curated place lanes. Keys added
 * later in Categories & attributes fall back to a lane with the same key
 * (`laneForCategory`), so new categories work without a code change.
 */
const CATEGORY_TO_LANE: Record<string, BusinessLaneKey> = {
  meeting: "coworking",
  coworking: "coworking",
  stays: "stays",
  events: "events",
  transport: "transport",
  restaurants: "dining",
  afterwork: "afterwork",
  wellness: "wellness",
};

export const Route = createFileRoute("/business/")({
  validateSearch: (search: Record<string, unknown>): BusinessSearch =>
    typeof search.lane === "string" && search.lane ? { lane: search.lane } : {},
  // Fetch the homepage payload during SSR so content is in the HTML instead of
  // arriving after hydration (this was the main "slow /business" cost).
  loader: ({ context }) =>
    (context as any).queryClient?.ensureQueryData({
      queryKey: ["business", "public-home"],
      queryFn: () => businessGetPublicHome(),
      staleTime: 60_000,
    }),

  head: () => {
    const url = "https://whatfinland.com/business";
    return {
      meta: [
        { title: "Business Travel In Finland — what Finland" },
        {
          name: "description",
          content:
            "A streamlined business mode: coworking spaces, business stays, networking events, transport and afterwork dining across Finland.",
        },
        { property: "og:title", content: "Business Travel In Finland — what Finland" },
        {
          property: "og:description",
          content: "Coworking, stays, networking events and afterwork picks for business travelers in Finland.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BusinessPage,
});

function BusinessPage() {
  const { lane: laneParam } = Route.useSearch();
  const navigate = useNavigate();
  const { city } = useCity();
  const cityName = useResolvedCity(city).name;
  const items = usePublicPlaces();
  const { lang } = useLanguage();

  // Live business content, managed in Business admin.
  const homeFn = useServerFn(businessGetPublicHome);
  const home = useQuery({
    queryKey: ["business", "public-home"],
    queryFn: () => homeFn(),
    staleTime: 60_000,
  });

  const pick = (en?: string | null, fi?: string | null) =>
    lang === "fi" && fi ? { text: fi, raw: true } : { text: en ?? "", raw: false };

  const partnerBanners = home.data?.partnerBanners ?? [];
  const categories = home.data?.categories ?? [];
  const allListings = home.data?.listings ?? [];
  const allServices = home.data?.services ?? [];
  const allEvents = home.data?.events ?? [];

  // Admins can switch any homepage section off in Business admin → Homepage.
  const hiddenSections = home.data?.hiddenSections ?? [];
  const sectionHidden = (key: string) => hiddenSections.includes(key);

  const copy = home.data?.copy ?? {};
  const c = (key: string, fallback: string) => {
    const fi = copy[`${key}_fi`];
    const en = copy[key];
    if (lang === "fi" && fi) return { text: fi, raw: true };
    return en ? { text: en, raw: false } : { text: fallback, raw: false };
  };

  const sectionTitle = (key: string, fallback: string) => {
    const s = home.data?.sections?.find((x) => x.key === key);
    if (!s) return { text: fallback, raw: false };
    return pick(s.title_en, s.title_fi);
  };

  // Analytics (read in Business admin → Dashboard).
  const trackFn = useServerFn(businessTrackEvent);
  const track = (event_type: string, extra: Record<string, string> = {}) => {
    void trackFn({
      data: { event_type: event_type as never, destination: city, ...extra, session_id: analyticsSessionId() },
    }).catch(() => {});
  };
  useEffect(() => {
    track("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  const [q, setQ] = useState("");
  const [catKey, setCatKey] = useState<string | undefined>(undefined);
  const [showCategories, setShowCategories] = useState(true);


  const activeCategory = categories.find((x) => x.key === catKey);
  const laneForCategory = (key: string) => CATEGORY_TO_LANE[key] ?? (findLane(key)?.key as BusinessLaneKey | undefined);
  const activeLane = findLane(laneParam ?? (catKey ? laneForCategory(catKey) : undefined));

  const setLane = (key: BusinessLaneKey | "") => {
    if (key === "events") {
      track("category_view");
      navigate({ to: "/business/events" });
      return;
    }
    if (key) track("category_view");
    navigate({ to: "/business", search: key ? { lane: key } : {}, resetScroll: false });
  };

  const onCategory = (cat: (typeof categories)[number] | null) => {
    if (cat) track("category_view", { category_id: cat.id });
    const lane = cat ? laneForCategory(cat.key) : undefined;
    if (lane === "events") {
      navigate({ to: "/business/events" });
      return;
    }
    if (cat) {
      // Each category has its own browsable page (city + EN/FI aware).
      navigate({ to: "/business/category/$key", params: { key: cat.key } });
      return;
    }
    setCatKey(undefined);
    navigate({ to: "/business", search: {}, resetScroll: false });
  };


  const needle = q.trim().toLowerCase();
  const inDestination = (destinations: string[] | null | undefined) =>
    !destinations || destinations.length === 0 || destinations.includes(city);

  // ---- real business-admin content, filtered by city + category + search ----
  const listings = useMemo(() => {
    return allListings.filter((l) => {
      if (!inDestination(l.destinations)) return false;
      // Category assignment comes from Business admin → Categories & attributes
      // (the listing ⇄ category join). Tags are only a legacy fallback for
      // listings that predate the taxonomy.
      if (activeCategory) {
        const ids = l.category_ids ?? [];
        const matched = ids.length
          ? ids.includes(activeCategory.id)
          : (l.tags ?? []).some((t) => t.toLowerCase().includes(activeCategory.key));
        if (!matched) return false;
      }
      if (!needle) return true;
      return [l.name, l.summary_en, l.summary_fi, l.address, (l.tags ?? []).join(" ")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allListings, city, activeCategory, needle]);





  const services = useMemo(() => {
    return allServices.filter((s) => {
      if (!inDestination(s.destinations)) return false;
      if (activeCategory && s.category_id && s.category_id !== activeCategory.id) return false;
      if (!needle) return true;
      return [s.name_en, s.name_fi, s.description_en].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allServices, city, activeCategory, needle]);

  const events = useMemo(() => {
    return allEvents.filter((e) => {
      if (e.destination && e.destination !== city) return false;
      if (activeCategory && e.category_id && e.category_id !== activeCategory.id) return false;
      if (!needle) return true;
      return [e.title_en, e.title_fi, e.venue_name].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, city, activeCategory, needle]);

  // ---- curated places ----
  // When an admin has hand-picked places for a lane (Business admin → Lanes),
  // that selection wins. Otherwise fall back to keyword matching.
  const lanePicks = home.data?.lanePicks;
  const inCity = useMemo(() => items.filter((i) => i.city === city), [items, city]);
  const byLane = useMemo(() => {
    const index = new Map(items.map((i) => [i.id, i]));
    return (lane: (typeof PRIMARY_LANES)[number]) => {
      const picked = lanePicks?.[lane.key];
      if (picked && picked.length > 0) {
        return picked.map((id) => index.get(id)).filter(Boolean) as typeof items;
      }
      return laneItems(inCity, lane);
    };
  }, [items, inCity, lanePicks]);


  const recommended = useMemo(() => {
    const base = activeLane
      ? byLane(activeLane)
      : PRIMARY_LANES.concat(AFTER_WORK_LANES).flatMap((l) => byLane(l).slice(0, 3));
    const seen = new Set<string>();
    const unique = base.filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
    if (!needle) return unique;
    return unique.filter((i) =>
      [i.name, i.category, i.area, i.description].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [byLane, activeLane, needle]);

  const filtersActive = Boolean(catKey || needle || laneParam);
  const clearAll = () => {
    setQ("");
    setCatKey(undefined);
    navigate({ to: "/business", search: {}, resetScroll: false });
  };

  return (
    <div className="pb-[calc(84px+env(safe-area-inset-bottom))] sm:pb-16">

      <ModeToggle mode="business" />

      {/* SEO: hidden page heading (visible hero band removed) */}
      <h1 className="sr-only" style={{ textTransform: "none" }}>
        <AutoT text="Business travel in Finland" />
      </h1>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6">

        {/* FEATURED PARTNERS — premium sponsored placement */}
        <PartnerBannerCarousel
          banners={partnerBanners}
          onCtaClick={() => track("cta_click")}
        />

        {/* CATEGORY TILES — "Find what your business needs" */}
        {showCategories && !sectionHidden("business_categories") ? (
          <section className="mt-6">
            <SectionHead
              title={sectionTitle("business_categories", "Find what your business needs").text}
              titleRaw={sectionTitle("business_categories", "Find what your business needs").raw}
              actionLabel=""
            />
            {home.isLoading ? (
              <CategoryTileGridSkeleton />
            ) : (
              <CategoryTileGrid categories={categories} activeKey={catKey} onSelect={onCategory} />
            )}
          </section>
        ) : null}

        {/* SEARCH + CATEGORY FILTERS */}
        <div className="sticky top-0 z-20 -mx-4 mt-5 border-b border-navy/5 bg-background/90 px-4 pb-2.5 pt-2.5 backdrop-blur-md sm:mx-0 sm:rounded-2xl sm:border-0 sm:px-4 sm:py-3">
          <div className="flex items-center gap-2.5 rounded-full border border-navy/10 bg-white px-4 py-2.5 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:px-5 sm:py-3">
            <Search className="size-[18px] shrink-0 text-navy/50" strokeWidth={2.25} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={c("search_placeholder", "Search coworking, stays, events").text}
              aria-label="Search business services"
              type="search"
              enterKeyHint="search"
              className="font-body h-6 min-w-0 flex-1 bg-transparent text-[16px] tracking-[-0.01em] text-navy outline-none placeholder:text-navy/40 sm:text-[15px] [&::-webkit-search-cancel-button]:hidden"
            />
            {filtersActive ? (
              <button
                type="button"
                onClick={clearAll}
                aria-label="Clear filters"
                className="grid size-7 shrink-0 place-items-center rounded-full bg-navy/5 text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <X className="size-3.5" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => setShowCategories((v) => !v)}
              aria-expanded={showCategories}
              aria-label="Toggle categories"
              className={`grid size-7 shrink-0 place-items-center rounded-full transition-colors ${
                showCategories ? "text-navy" : "text-navy/45 hover:text-navy"
              }`}
            >
              <SlidersHorizontal className="size-[18px]" strokeWidth={2.25} />
            </button>
          </div>

        </div>


        {/* ERROR STATE */}
        {home.isError ? (
          <div className="mt-6">
            <ErrorState
              title="We couldn't load business content."
              description="Check your connection and try again — curated places below still work offline of this feed."
              onRetry={() => void home.refetch()}
              retrying={home.isFetching}
            />
          </div>
        ) : null}

        {/* BUSINESS DIRECTORY — live listings from Business admin */}
        {sectionHidden("business_directory") ? null : home.isLoading ? (
          <section className="mt-8">
            <SectionHead title="Business directory" actionLabel="" />
            <CardSkeleton count={6} />
          </section>
        ) : listings.length > 0 ? (
          <section className="mt-8">
            <SectionHead
              title={sectionTitle("business_directory", "Business directory").text}
              titleRaw={sectionTitle("business_directory", "Business directory").raw}
              count={listings.length}
              actionLabel={filtersActive ? "Clear" : ""}
              onAction={clearAll}
            />
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
              {listings.slice(0, 24).map((l) => (
                <BusinessListingCard
                  key={l.id}
                  listing={l}
                  onOpen={(x) => track("website_click", { listing_id: x.id })}
                />
              ))}
            </div>
          </section>
        ) : !home.isError && allListings.length > 0 ? (
          <section className="mt-8">
            <SectionHead
              title={sectionTitle("business_directory", "Business directory").text}
              titleRaw={sectionTitle("business_directory", "Business directory").raw}
              actionLabel=""
            />
            <EmptyState
              title="No business listings match this filter"
              description={
                filtersActive
                  ? "Try a different category, clear your search, or switch city."
                  : `Nothing published for ${cityName} yet — try another city.`
              }
              actionLabel={filtersActive ? "Clear filters" : undefined}
              onAction={filtersActive ? clearAll : undefined}
            />
          </section>
        ) : null}

        {/* SERVICES */}
        {sectionHidden("business_services") ? null : home.isLoading ? (
          <section className="mt-10">
            <SectionHead title="Services for companies" actionLabel="" />
            <ServiceSkeleton />
          </section>
        ) : services.length > 0 ? (
          <section className="mt-10">
            <SectionHead
              title={sectionTitle("business_services", "Services for companies").text}
              titleRaw={sectionTitle("business_services", "Services for companies").raw}
              count={services.length}
              actionLabel=""
            />
            <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {services.slice(0, 12).map((s) => (
                <ServiceCard key={s.id} service={s} onOpen={(x) => track("click", { service_id: x.id })} />
              ))}
            </div>
          </section>
        ) : null}

        {/* RECOMMENDED FOR YOU — hidden when the admin switches the section off */}
        {sectionHidden("business_recommended") ? null : (
        <section className="mt-10">
          <SectionHead
            title={activeLane ? activeLane.label : c("results_title", "Recommended for you").text}
            titleRaw={!activeLane && c("results_title", "Recommended for you").raw}
            count={recommended.length}
            actionLabel={filtersActive ? "Clear" : ""}
            onAction={clearAll}
          />

          {items.length === 0 ? (
            <CardSkeleton count={6} />
          ) : recommended.length === 0 ? (
            <EmptyState
              title="Nothing curated here yet"
              description={
                filtersActive
                  ? "Try another category, clear your search, or switch city."
                  : `We haven't curated picks for ${cityName} yet — switch city to explore more.`
              }
              actionLabel={filtersActive ? "Clear filters" : undefined}
              onAction={filtersActive ? clearAll : undefined}
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
              {recommended.slice(0, 24).map((i) => (
                <PlaceCard key={i.id} item={i} compact />
              ))}
            </div>
          )}
        </section>
        )}

        {/* UPCOMING BUSINESS EVENTS — dark panel */}
        {sectionHidden("business_events") ? null : home.isLoading && !home.data ? (

          <section className="mt-10">
            <SectionHead title="Upcoming business events" actionLabel="" />
            <EventSkeleton />
          </section>
        ) : events.length > 0 ? (
          <section className="mt-10">
            <EventsPanel
              title={sectionTitle("business_events", "Upcoming business events").text}
              titleRaw={sectionTitle("business_events", "Upcoming business events").raw}
              events={events.slice(0, 6)}
              onOpen={(x) => track("event_view", { event_id: x.id })}
              onSeeAll={() => navigate({ to: "/business/events" })}
            />
          </section>
        ) : null}

      </div>
    </div>
  );
}

function SectionHead({
  title,
  titleRaw,
  actionLabel,
  onAction,
  count,
}: {
  title: string;
  titleRaw?: boolean;
  actionLabel: string;
  onAction?: () => void;
  count?: number;
}) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2
        style={{ textTransform: "none" }}
        className="flex min-w-0 items-center gap-2 font-heading text-xl font-bold text-navy sm:text-2xl"
      >
        <span className="truncate">{titleRaw ? title : <AutoT text={title} />}</span>
        {typeof count === "number" && count > 0 ? (
          <span className="shrink-0 rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-semibold text-navy/60">
            {count}
          </span>
        ) : null}
      </h2>

      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{ textTransform: "none" }}
          className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-navy transition-colors hover:text-royal"
        >
          <AutoT text={actionLabel} />
          <ChevronRight className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
